<script setup lang="ts">
import Product from '@/api/Product'
import DiscountBadge from '@/components/common/DiscountBadge.vue'
import DisplayBanner from '@/components/common/DisplayBanner.vue'
import ProductRatingSold from '@/components/common/ProductRatingSold.vue'
import TextOverflowAnywhere from '@/components/common/TextOverflowAnywhere.vue'
import DiscoverSection from '@/components/common/discover/DiscoverSection.vue'
import Error404 from '@/components/common/errors/404Error.vue'
import ProductDetailsSkeleton from '@/components/common/skeleton/ProductDetailsSkeleton.vue'
import MobileProductImages from '@/components/product-details/MobileProductImages.vue'
import MobilePurchaseOptions from '@/components/product-details/MobilePurchaseOptions.vue'
import OfficialMerchant from '@/components/product-details/OfficialMerchant.vue'
import ProductImages from '@/components/product-details/ProductImages.vue'
import PurchaseOptions from '@/components/product-details/PurchaseOptions.vue'
import QuickActions from '@/components/product-details/QuickActions.vue'
import SimilarProducts from '@/components/product-details/SimilarProducts.vue'
import { usePrice } from '@/composables/price'
import { useScreenSize } from '@/composables/screen-size'
import { useTitle } from '@/composables/title'
import { useTracker } from '@/composables/tracker'
import useAppStore from '@/stores/app'
import useAuthStore from '@/stores/auth'
import useCartStore from '@/stores/cart'
import useProductStore from '@/stores/product'
import { AppDivider, AppSection } from 'lilasia-ui'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate } from 'vue-router'

const props = defineProps<{
  id: number
  slug: string
}>()

const { setTitle } = useTitle()
const { formatMoney } = usePrice()
const { isMobile, isDesktop } = useScreenSize()
const { trackCategoryView, trackProductView } = useTracker()
const { t } = useI18n()

const appStore = useAppStore()
const authStore = useAuthStore()
const productStore = useProductStore()
const cartStore = useCartStore()

const loading = ref(true)
const notFound = ref(false)
const productSectionEnd = ref<HTMLElement>()

const product = computed(() => productStore.selectedProduct)
const decoyPrice = computed(() => product.value?.promotions?.[0]?.decoy_price)

const categories = computed(() => {
  if (product.value?.categories && product.value?.categories[0]) {
    return product.value?.categories.map((category) => category.name).join(', ')
  }

  return null
})

watch(
  () => appStore.showMobileMenu,
  (showMobileMenu) => {
    productStore.showQuickAction = !showMobileMenu
    handleScroll()
  }
)

onMounted(() => {
  if (!productStore.redirectedFromGallery) {
    productStore.activeImageIndex = 0
    fetchProduct(props.id)
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  productStore.selectedProduct = null
  productStore.redirectedFromGallery = false
})

onBeforeRouteUpdate((to) => {
  loading.value = true
  productStore.selectedProduct = null
  productStore.activeImageIndex = 0
  productStore.redirectedFromGallery = false
  cartStore.quantity = 1
  fetchProduct(Number(to.params.id))
})

const fetchProduct = async (id: number) => {
  try {
    productStore.selectedProduct = await Product.show(id)
    if (productStore.selectedProduct) {
      setTitle(productStore.selectedProduct.name)

      await trackProductView(productStore.selectedProduct, authStore.user?.id)

      if (
        productStore.selectedProduct.categories?.length &&
        productStore.selectedProduct.categories?.length > 0
      ) {
        await trackCategoryView(productStore.selectedProduct.categories)
      }
    }
  } catch (error) {
    const err = error as FetchError
    if (404 === err.code) {
      notFound.value = true
    }
  } finally {
    loading.value = false
  }
}

const handleScroll = () => {
  if (!appStore.showMobileMenu) {
    const targetScrollHeight = productSectionEnd.value?.scrollHeight || 0
    if (window.scrollY > targetScrollHeight) {
      productStore.showQuickAction = false
    } else {
      productStore.showQuickAction = true
    }
  }
}
</script>

<template>
  <div>
    <template v-if="product">
      <AppSection
        :fluid="isMobile"
        :no-gap="isMobile"
      >
        <div class="md:grid md:grid-cols-5 md:items-start md:gap-24">
          <div class="w-full md:col-span-3">
            <!-- Images -->
            <ProductImages
              v-if="isDesktop"
              :product="product"
            />
            <MobileProductImages
              v-else
              :product="product"
            />

            <!-- Details -->
            <div
              class="mt-24"
              :class="isMobile ? 'container mx-auto' : ''"
            >
              <div class="flex flex-col gap-16">
                <div class="flex flex-col gap-8">
                  <div
                    v-if="categories"
                    class="text-14 font-600 leading-20 text-black-60"
                  >
                    {{ categories }}
                  </div>
                  <TextOverflowAnywhere class="text-20 font-500">
                    {{ product.name }}
                  </TextOverflowAnywhere>
                  <ProductRatingSold
                    :rating="product.rating"
                    :sold="product.sold"
                    large
                  />
                  <div class="text-32 font-700 leading-[42px] text-blue-100">
                    {{ formatMoney(product.price) }}
                  </div>
                  <DiscountBadge
                    :price="product.price"
                    :decoy-price="decoyPrice"
                    large
                  />
                </div>
                <AppDivider />
                <div class="flex flex-col gap-8">
                  <div class="leadng-20 text-14 font-500">
                    {{ t('description') }}
                  </div>
                  <!-- eslint-disable vue/no-v-html -->
                  <TextOverflowAnywhere>
                    <div
                      ref="productSectionEnd"
                      class="text-16 leading-24 text-black-60"
                      v-html="product.description"
                    ></div>
                  </TextOverflowAnywhere>
                </div>
                <OfficialMerchant class="md:hidden" />
              </div>
            </div>
          </div>

          <div
            v-if="!isMobile"
            class="sticky top-[96px] inline-block md:col-span-2 lg:top-[112px]"
          >
            <div class="flex flex-col md:gap-16 lg:gap-24">
              <OfficialMerchant />
              <PurchaseOptions />
            </div>
          </div>
        </div>
      </AppSection>

      <SimilarProducts
        v-if="product.id"
        :product-id="product.id"
      />

      <DisplayBanner />

      <DiscoverSection hide-highlights />

      <DisplayBanner />

      <Teleport to="body">
        <QuickActions />
        <MobilePurchaseOptions />
      </Teleport>
    </template>

    <ProductDetailsSkeleton v-else-if="loading" />
    <Error404 v-else-if="notFound" />
  </div>
</template>
